import { forwardRef, InputHTMLAttributes, MouseEvent } from 'react';

import * as css from './Checkbox.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    /** Label text displayed next to the checkbox. */
    label: string;

    /** Applies secondary visual styling. */
    secondary?: boolean;

    // modal styling
    modal?: boolean;

    /** Makes the checkbox take full available width. */
    fullWidth?: boolean;

    /** Optional action button shown when the checkbox is checked. */
    action?: {
        /** Text displayed inside the action button. */
        label: string;

        /** Applies active styling to the action button. */
        active?: boolean;

        /** Click handler for the action button. */
        onClick: (e: MouseEvent<HTMLButtonElement>) => void;
    };
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ label, secondary, fullWidth, action, checked, modal, ...props }, ref) => {
        const handleActionClick = (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            action?.onClick(e);
        };

        return (
            <div className={css.container({ secondary, modal, fullWidth })}>
                <label className={css.label}>
                    <input
                        type="checkbox"
                        ref={ref}
                        checked={checked}
                        {...props}
                        className={css.input[modal ? 'inverse' : 'default']}
                    />
                    <span className={css.text}>{label}</span>
                    {checked && action && (
                        <button
                            type="button"
                            className={css.action({ active: action.active })}
                            onClick={handleActionClick}
                        >
                            {action.label}
                        </button>
                    )}
                </label>
            </div>
        );
    },
);

Checkbox.displayName = 'Checkbox';
